import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { createApiFetch } from "../auth/apiFetch";
import { buildApiUrl } from "../runtimeConfig";
import { normalizeTimezoneSlug } from "../utils/timezones";
import { normalizeIsoCountry } from "../lib/countries";
import { useAuth } from "./AuthContext";

export type CountryCode = 
  | "austria" | "belgium" | "bulgaria" | "croatia" | "cyprus"
  | "czechia" | "denmark" | "estonia" | "finland" | "france"
  | "germany" | "greece" | "hungary" | "ireland" | "italy"
  | "latvia" | "lithuania" | "luxembourg" | "malta" | "netherlands"
  | "poland" | "portugal" | "romania" | "slovakia" | "slovenia"
  | "spain" | "sweden";

interface RegionalSettings {
  /**
   * The organization's country as a slug, for the per-country tables keyed that way (VAT formats,
   * address examples). "" when the organization has no country — legacy sign-ups created it with
   * CountryCode "" — or a country outside the slug table. "" is never Portugal.
   */
  country: CountryCode | "";
  /** The organization's ISO 3166-1 alpha-2 country, upper case; "" when it has none. */
  countryIso: string;
  language: string;
  timezone: string;
  currency: string;
}

interface RegionalSettingsContextType {
  settings: RegionalSettings;
  /** True once `settings` holds the server's values; until then they are the defaults (e.g. currency "eur"). */
  isLoaded: boolean;
  refresh: () => Promise<void>;
  /** Saves the organization's country (ISO alpha-2) together with the current currency. */
  updateCountry: (isoCountry: string) => Promise<void>;
  updateLanguage: (language: string) => Promise<void>;
  updateTimezone: (timezone: string) => Promise<void>;
  /** Rejects with OrganizationCountryRequiredError, without a request, while the org has no country. */
  updateCurrency: (currency: string) => Promise<void>;
}

/**
 * The organization endpoint saves country and currency together, so with no country there is
 * nothing honest to send: not "", which the API refuses, and not a made-up default.
 */
export class OrganizationCountryRequiredError extends Error {
  constructor() {
    super("The organization has no country; choose one before saving regional settings.");
    this.name = "OrganizationCountryRequiredError";
  }
}

const RegionalSettingsContext = createContext<RegionalSettingsContextType | undefined>(undefined);

type RegionalSettingsResponse = {
  user?: {
    language?: string | null;
    timezone?: string | null;
  };
  organization?: {
    country?: string | null;
    currency?: string | null;
  };
};

const ISO_TO_COUNTRY: Record<string, CountryCode> = {
  AT: "austria",
  BE: "belgium",
  BG: "bulgaria",
  HR: "croatia",
  CY: "cyprus",
  CZ: "czechia",
  DK: "denmark",
  EE: "estonia",
  FI: "finland",
  FR: "france",
  DE: "germany",
  GR: "greece",
  HU: "hungary",
  IE: "ireland",
  IT: "italy",
  LV: "latvia",
  LT: "lithuania",
  LU: "luxembourg",
  MT: "malta",
  NL: "netherlands",
  PL: "poland",
  PT: "portugal",
  RO: "romania",
  SK: "slovakia",
  SI: "slovenia",
  ES: "spain",
  SE: "sweden",
};

const COUNTRY_TO_ISO: Record<CountryCode, string> = {
  austria: "AT",
  belgium: "BE",
  bulgaria: "BG",
  croatia: "HR",
  cyprus: "CY",
  czechia: "CZ",
  denmark: "DK",
  estonia: "EE",
  finland: "FI",
  france: "FR",
  germany: "DE",
  greece: "GR",
  hungary: "HU",
  ireland: "IE",
  italy: "IT",
  latvia: "LV",
  lithuania: "LT",
  luxembourg: "LU",
  malta: "MT",
  netherlands: "NL",
  poland: "PL",
  portugal: "PT",
  romania: "RO",
  slovakia: "SK",
  slovenia: "SI",
  spain: "ES",
  sweden: "SE",
};

// No country until the server says so: an organization without one stays without one.
const DEFAULT_SETTINGS: RegionalSettings = {
  country: "",
  countryIso: "",
  language: "en",
  timezone: "europe-lisbon",
  currency: "eur",
};

/**
 * ISO alpha-2 code for a country slug ("portugal" -> "PT"). "" when there is no country or the slug
 * is unknown — never a stand-in: an empty country means ask the user, not assume one. Callers
 * building a formatting locale use the bare language then ("pt", not "pt-").
 */
export const toIsoCountryCode = (country?: CountryCode | "" | null): string => {
  if (!country) {
    return "";
  }

  return COUNTRY_TO_ISO[country] ?? "";
};

const toCountrySlug = (isoCountry: string): CountryCode | "" => ISO_TO_COUNTRY[isoCountry] ?? "";

const normalizeCurrency = (currency?: string | null): string => {
  if (!currency) {
    return DEFAULT_SETTINGS.currency;
  }

  return currency.trim().toLowerCase();
};

export function RegionalSettingsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<RegionalSettings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);
  const apiFetch = useMemo(() => createApiFetch(8000), []);

  const applyPayload = useCallback((payload: RegionalSettingsResponse) => {
    const countryIso = normalizeIsoCountry(payload.organization?.country);
    setSettings((current) => ({
      country: toCountrySlug(countryIso),
      countryIso,
      currency: normalizeCurrency(payload.organization?.currency),
      language: payload.user?.language?.trim().toLowerCase() || current.language,
      timezone: normalizeTimezoneSlug(payload.user?.timezone) || current.timezone,
    }));
    setIsLoaded(true);
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setSettings(DEFAULT_SETTINGS);
      setIsLoaded(false);
      return;
    }

    try {
      const response = await apiFetch(buildApiUrl("/api/settings/regional"), {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to load regional settings.");
      }

      const payload = await response.json() as RegionalSettingsResponse;
      applyPayload(payload);
    } catch (error) {
      console.error("Failed to load regional settings.", error);
    }
  }, [apiFetch, applyPayload, isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateCountry = useCallback(
    async (country: string) => {
      const isoCountry = normalizeIsoCountry(country);
      if (!isoCountry) {
        throw new OrganizationCountryRequiredError();
      }
      const response = await apiFetch(buildApiUrl("/api/settings/regional/organization"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          country: isoCountry,
          currency: settings.currency.toUpperCase(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update organization settings.");
      }

      setSettings((prev) => ({ ...prev, country: toCountrySlug(isoCountry), countryIso: isoCountry }));
    },
    [apiFetch, settings.currency],
  );

  const updateCurrency = useCallback(
    async (currency: string) => {
      if (!settings.countryIso) {
        throw new OrganizationCountryRequiredError();
      }
      const response = await apiFetch(buildApiUrl("/api/settings/regional/organization"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          country: settings.countryIso,
          currency: currency.toUpperCase(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update organization settings.");
      }

      setSettings((prev) => ({ ...prev, currency }));
    },
    [apiFetch, settings.countryIso],
  );

  const updateLanguage = useCallback(
    async (language: string) => {
      const response = await apiFetch(buildApiUrl("/api/settings/regional/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          language: language.toLowerCase(),
          timezone: settings.timezone,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update user settings.");
      }

      setSettings((prev) => ({ ...prev, language }));
    },
    [apiFetch, settings.timezone],
  );

  const updateTimezone = useCallback(
    async (timezone: string) => {
      const response = await apiFetch(buildApiUrl("/api/settings/regional/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          language: settings.language,
          timezone,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update user settings.");
      }

      setSettings((prev) => ({ ...prev, timezone }));
    },
    [apiFetch, settings.language],
  );

  return (
    <RegionalSettingsContext.Provider 
      value={{ 
        settings,
        isLoaded,
        refresh,
        updateCountry,
        updateLanguage,
        updateTimezone,
        updateCurrency 
      }}
    >
      {children}
    </RegionalSettingsContext.Provider>
  );
}

export function useRegionalSettings() {
  const context = useContext(RegionalSettingsContext);
  if (context === undefined) {
    throw new Error("useRegionalSettings must be used within a RegionalSettingsProvider");
  }
  return context;
}
